import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import {
  Divider,
  Grid,
  IconButton,
  Input,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  Link as MuiLink,
} from '@mui/material';
import {
  Add as CreateIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Done as SaveIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  NorthEast as NorthEastIcon,
  SouthWest as SouthWestIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

import { Table } from '../../components/table/table';
import { CreateFeeModal, DeleteFeeModal } from './components';
import { HeadCell, Row, RowCell } from '../../components/table/types';

import { UTCToDateTime } from '../../utils/dates';
import { useAppActions } from '../../utils/hooks';
import { Option, TransactionDirection } from '../../enums';
import { layoutAction } from '../../store/sideBarStore/layoutStore';
import {
  PostBlockchainFeeMarkupDTO,
  PutBlockchainFeeMarkupByIdDTO,
  useDeleteBlockchainFeeMarkupMutation,
  useGetBlockchainFeeMarkupsQuery,
  usePostBlockchainFeeMarkupMutation,
  usePutBlockchainFeeMarkupMutation,
} from '../../api/controllers/blockchain-fee-markups';
import { permissionsCheck } from '../../utils/permission';
import { formatAmount, formatBlockchainAmountWithCurrency } from '../../utils';

export const BlockchainFeeMarkups = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const hasPermissionToEditMarkups = permissionsCheck(['WRITE_BLOCKCHAIN_FEE_MARKUPS']);

  // api
  const { data: feeList, isLoading: isFeeLoading, refetch } = useGetBlockchainFeeMarkupsQuery();
  const [createBlockchainFeeMarkup, { isLoading: isCreateBlockchainFeeMarkupLoading }] =
    usePostBlockchainFeeMarkupMutation();
  const [putFee, { isLoading: isFeePutLoading }] = usePutBlockchainFeeMarkupMutation();
  const [deleteFee, { isLoading: isFeeDeleteLoading }] = useDeleteBlockchainFeeMarkupMutation();

  // variables
  const isLoading = isFeeLoading || isFeePutLoading || isFeeDeleteLoading;

  // edit mode logic
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const [isEditMode, setEditMode] = useState(false);
  const startEditMode = (fee: any) => {
    setEditFields(fee as unknown as PutBlockchainFeeMarkupByIdDTO['body']);
    setActiveRowId(fee.id);
    setEditMode(true);
  };
  const offEditMode = () => {
    setEditFields(null);
    setActiveRowId(null);
    setEditMode(false);
  };

  // show action button
  const { setSpeedDialButtonComponent, removeSpeedDialButtonContent } = useAppActions(layoutAction);
  useEffect(() => {
    hasPermissionToEditMarkups &&
      setSpeedDialButtonComponent([
        { icon: <CreateIcon />, onClick: openCreateModal, disabled: isEditMode },
      ]);

    return () => {
      removeSpeedDialButtonContent();
    };
  }, [isEditMode]);

  // edit fields logic
  const [createFields, setCreateFields] = useState<PostBlockchainFeeMarkupDTO | null>(null);
  const handleEditCreateFields = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const name = e.target.name;
    const value = e.target.value;

    // @ts-ignore
    setCreateFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // edit fields logic
  const [editFields, setEditFields] = useState<PutBlockchainFeeMarkupByIdDTO['body'] | null>(null);
  const handleEditFields = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const name = e.target.name;
    const value = e.target.value;

    // @ts-ignore
    setEditFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // add fee modal logic
  const [createModalShown, setCreateModalShown] = useState(false);
  const openCreateModal = () => {
    setCreateModalShown(true);
    // setEditFields(null);
  };
  const closeCreateModal = () => {
    setCreateModalShown(false);
    setEditFields(null);
  };

  // delete fee modal logic
  const [deleteModalShown, setDeleteModalShown] = useState(false);
  const openDeleteModal = (id: string) => {
    setDeleteModalShown(true);
    setActiveRowId(id);
  };
  const closeDeleteModal = () => {
    setDeleteModalShown(false);
    setActiveRowId(null);
  };

  // edit fee handler
  const editFee = async () => {
    if (!activeRowId || !editFields) return;

    const args = {
      blockchain_fee_markup_id: activeRowId,
      body: {
        fixed_amount: String(editFields.fixed_amount),
        percent: String(editFields.percent),
        option: editFields.option,
      },
    };

    try {
      await putFee(args).unwrap();
      offEditMode();
      refetch();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // create fee handler
  const createFee = async () => {
    if (!createFields) return;

    const args = {
      coin_id: createFields.coin_id,
      fixed_amount: String(createFields.fixed_amount),
      percent: String(createFields.percent),
      option: createFields.option,
    };

    try {
      await createBlockchainFeeMarkup(args).unwrap();
      closeCreateModal();
      refetch();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // delete fee handler
  const deleteFeeHandler = async () => {
    if (!activeRowId) return;

    const args = { blockchain_fee_markup_id: activeRowId };

    try {
      await deleteFee(args).unwrap();
      closeDeleteModal();
      refetch();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // table cells and rows
  const headCells: HeadCell[] = [
    { id: '1', label: t('blockchain-fee-markups.head-cells.coin') },
    { id: '2', label: t('blockchain-fee-markups.head-cells.fixed-amount'), align: 'right' },
    { id: '3', label: t('blockchain-fee-markups.head-cells.option') },
    { id: '4', label: t('blockchain-fee-markups.head-cells.percent'), align: 'right' },
    { id: '5', label: t('blockchain-fee-markups.head-cells.row-start') },
    {
      id: '6',
      label: t('blockchain-fee-markups.head-cells.actions'),
      hidden: !hasPermissionToEditMarkups,
    },
  ];

  const rows: Row[] | undefined = feeList?.blockchainFeeMarkups.map((it) => {
    const { id, coin, fixed_amount, option, percent, row_start } = it;

    const cells: RowCell[] = [
      { name: 'coin_name', value: coin.name },
      {
        name: 'fixed_amount',
        value: formatAmount(fixed_amount),
        align: 'right',
        component:
          isEditMode && activeRowId === id ? (
            <Input
              name="fixed_amount"
              type="number"
              value={editFields?.fixed_amount ?? ''}
              onChange={(e) => handleEditFields(e)}
              inputProps={{ min: 0, step: '0.01' }}
              sx={{ maxWidth: '100px' }}
              disabled={isFeePutLoading}
            />
          ) : undefined,
      },
      {
        name: 'option',
        value: option,
        component:
          isEditMode && activeRowId === id ? (
            <Select
              name="option"
              value={editFields?.option ?? ''}
              onChange={(e) => handleEditFields(e)}
              variant="standard"
              sx={{ width: '80px' }}
              disabled={isFeePutLoading}
            >
              {Object.values(Option).map((it) => {
                return (
                  <MenuItem key={it} value={it}>
                    {it}
                  </MenuItem>
                );
              })}
            </Select>
          ) : undefined,
      },
      {
        name: 'percent',
        value: percent,
        align: 'right',
        formatting: (value: string) => {
          return `${value} %`;
        },
        component:
          isEditMode && activeRowId === id ? (
            <Input
              name="percent"
              type="number"
              value={editFields?.percent ?? ''}
              onChange={handleEditFields}
              inputProps={{ min: 0, step: '0.01' }}
              endAdornment="%"
              sx={{ maxWidth: '100px' }}
              disabled={isFeePutLoading}
            />
          ) : undefined,
      },
      {
        name: 'row_start',
        value: row_start,
        formatting: (value: string) => {
          return UTCToDateTime(value);
        },
      },
      {
        name: 'actions',
        hidden: !hasPermissionToEditMarkups,
        component: isEditMode ? (
          activeRowId === id ? (
            <Stack direction="row" spacing={2}>
              <IconButton
                aria-label="save"
                color="success"
                onClick={() => editFee()}
                disabled={isFeePutLoading}
              >
                <SaveIcon />
              </IconButton>

              <IconButton
                aria-label="cancel"
                color="error"
                onClick={() => offEditMode()}
                disabled={isFeePutLoading}
              >
                <CancelIcon />
              </IconButton>
            </Stack>
          ) : undefined
        ) : (
          <Stack direction="row" spacing={2}>
            <IconButton
              aria-label="edit"
              color="warning"
              onClick={() => startEditMode(it)}
              disabled={isLoading}
            >
              <EditIcon />
            </IconButton>

            <IconButton
              aria-label="delete"
              color="error"
              onClick={() => openDeleteModal(id)}
              disabled={isLoading}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ),
      },
    ];

    return { cells };
  });

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} />
        </Paper>
      </Grid>

      <DeleteFeeModal
        open={deleteModalShown}
        onClose={closeDeleteModal}
        onDelete={deleteFeeHandler}
        isLoading={isFeeDeleteLoading}
      />

      <CreateFeeModal
        open={createModalShown}
        onClose={closeCreateModal}
        onCreate={createFee}
        fields={createFields}
        fieldsHandler={handleEditCreateFields}
        isLoading={isFeePutLoading}
      />
    </>
  );
};
